// ReportsArchiveScreen — searchable, paginated archive of leader digests
// (docs/miniapp-m6-tz.md §3.7). Director-only. Live against slice-A
// GET /reports (q + kind + page). Debounced search, kind chips, "load more"
// (infinite query) for 100+ records. Tap → /reports/:id markdown render.
import { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { fetchReports } from '../../api/reports'
import type { ArchiveReport } from '../../api/types'
import { LastUpdated } from '../../components/LastUpdated'
import { Badge, Button, EmptyState, Icon, PullToRefresh, ScreenSkeleton, t, useToast } from '../../design'

function fmtDate(iso: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '')
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || '')
}

function kindLabel(kind: string): string {
  if (kind.includes('weekly')) return t('air.weekly')
  if (kind.includes('calls')) return t('cs.title')
  return t('air.daily')
}

type KindFilter = 'all' | 'daily' | 'weekly'
const KIND_VALUE: Record<KindFilter, string | null> = {
  all: null, daily: 'daily_leader_digest', weekly: 'weekly_summary',
}

function ReportCard({ r, onClick }: { r: ArchiveReport; onClick: () => void }) {
  const weekly = r.kind.includes('weekly')
  return (
    <div className="card card-16" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="row middle gap-2" style={{ marginBottom: 6 }}>
        <Badge tone={weekly ? 'ai' : 'neutral'} sm>
          <Icon.Activity size={10} sw={2.5} />{kindLabel(r.kind)}
        </Badge>
        {r.generated_at && <span className="t-caption dim flex-1" style={{ textAlign: 'right' }}>{fmtDate(r.generated_at)}</span>}
      </div>
      <div className="t-body-s" style={{ marginBottom: 4 }}>{r.title}</div>
      <div className="t-caption" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {r.excerpt}
      </div>
    </div>
  )
}

export function ReportsArchiveScreen() {
  const navigate = useNavigate()
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [kind, setKind] = useState<KindFilter>('all')

  // 300ms debounce (TZ §10.2 reports_archive_search_debounces).
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(search), 300)
    return () => clearTimeout(id)
  }, [search])

  const {
    data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, dataUpdatedAt,
  } = useInfiniteQuery({
    queryKey: ['reports', debouncedQ, kind],
    queryFn: ({ pageParam }) => fetchReports({ q: debouncedQ, kind: KIND_VALUE[kind], page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.reports.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
  })

  const reports = data?.pages.flatMap((p) => p.reports) ?? []
  const total = data?.pages[0]?.total ?? 0
  const searching = debouncedQ.trim() !== '' || kind !== 'all'

  const KINDS: { id: KindFilter; label: string }[] = [
    { id: 'all', label: t('arch.all') },
    { id: 'daily', label: t('air.daily') },
    { id: 'weekly', label: t('air.weekly') },
  ]

  const onRefresh = async () => {
    await refetch()
    toast(t('ptr.updated'))
  }

  return (
    <PullToRefresh onRefresh={onRefresh} className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
      <div className="t-h1" style={{ marginBottom: 2 }}>{t('arch.title')}</div>
      <div style={{ marginBottom: 10 }}><LastUpdated at={dataUpdatedAt} /></div>

      {/* Search */}
      <div className="row middle gap-2" style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
        padding: '8px 12px', marginBottom: 10,
      }}>
        <Icon.Search size={16} sw={2} style={{ color: 'var(--text-3)' }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('arch.search')}
          style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'var(--text-1)', font: 'inherit' }}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{ border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', padding: 0 }}>
            <Icon.X size={15} sw={2.5} />
          </button>
        )}
      </div>

      {/* Kind chips */}
      <div className="row gap-2" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        {KINDS.map((k) => {
          const active = k.id === kind
          return (
            <button
              key={k.id}
              className="chip"
              onClick={() => setKind(k.id)}
              style={{
                cursor: 'pointer', border: 'none',
                background: active ? 'var(--brand-100)' : 'var(--surface-2)',
                color: active ? 'var(--brand-900)' : 'var(--text-2)',
                fontWeight: active ? 700 : 500,
              }}
            >
              {k.label}
            </button>
          )
        })}
        {!isLoading && total > 0 && (
          <span className="t-caption dim" style={{ alignSelf: 'center', marginLeft: 'auto' }}>{t('arch.count', { n: total })}</span>
        )}
      </div>

      {isLoading ? (
        <ScreenSkeleton kind="list" />
      ) : isError ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: 24 }}>
          <Icon.CloudOff size={30} sw={2} style={{ color: 'var(--text-3)' }} />
          <div className="t-h2">{t('arch.loadErr')}</div>
          <Button variant="secondary" icon={<Icon.Refresh size={16} sw={2} />} onClick={() => refetch()}>{t('btn.retry')}</Button>
        </div>
      ) : reports.length === 0 ? (
        <EmptyState
          icon={<Icon.Activity size={28} />}
          title={searching ? t('arch.searchEmpty') : t('arch.empty')}
          hint={searching ? undefined : t('arch.emptyHint')}
        />
      ) : (
        <>
          <div className="col gap-2">
            {reports.map((r) => (
              <ReportCard key={r.id} r={r} onClick={() => navigate(`/reports/${encodeURIComponent(r.id)}`)} />
            ))}
          </div>
          {hasNextPage && (
            <Button
              variant="ghost" full onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
              style={{ marginTop: 12 }}
            >
              <Icon.ArrowDown size={15} sw={2.25} />{t('arch.loadMore')}
            </Button>
          )}
        </>
      )}
    </PullToRefresh>
  )
}
